import {
  type ListSuppliersBody,
  type ListSuppliersResponse,
  type ListTaxonAttributesBody,
  type ListTaxonAttributesResponse,
  type Months,
  Taxon,
  type TaxonDTO,
} from '@psw/shared';
import { Attribute } from '../Models/Attribute';
import { TaxonMonth } from '../Models/TaxonMonth.model';
import { type FetchError, FetchService, queryClient } from '../services/fetch.service';
import { type PswQueryOptions, usePswQuery } from './queries';

export const taxonQueriesKeys = {
  taxonAttributes: (taxonKey?: string, suppliers?: string[], xpath?: string) =>
    ['taxonAttributes', taxonKey, suppliers, xpath] as const,
  taxons: (dateRange: number[], suppliers: string[]) => ['taxons', dateRange, suppliers] as const,
};

// =====================================================================================================================
// List taxons
// =====================================================================================================================

const transformRawDataToTaxon = (data: TaxonDTO) => new Taxon(data);

export function useTaxons(dateRange: number[], suppliers: string[], options?: PswQueryOptions<TaxonDTO, Taxon>) {
  return usePswQuery({
    ...options,
    queryKey: taxonQueriesKeys.taxons(dateRange, suppliers),
    queryFn: () =>
      FetchService.post<TaxonDTO>('/taxons', {
        fromDate: dateRange[0],
        toDate: dateRange[1],
        suppliers,
      }),
    enabled:
      options && 'enabled' in options
        ? options.enabled
        : dateRange.length === 2 && dateRange[0] !== 0 && dateRange[1] !== 0,
    select: transformRawDataToTaxon,
  });
}

// =====================================================================================================================
// Get taxons attributes
// =====================================================================================================================

export type ListTaxonAttributesTransformedData = Omit<ListTaxonAttributesResponse, 'filters'> & {
  filters: Attribute[];
};

const transformRawAttributesData = (data: ListTaxonAttributesResponse) => ({
  filters: data.filters.map(filter => new Attribute(filter)),
  allergyCompliances: data.allergyCompliances,
  accreditations: data.accreditations,
});

export function useTaxonAttributes(params: {
  taxonKey: string;
  suppliers?: string[];
  xpath?: string;
  options?: PswQueryOptions<ListTaxonAttributesResponse, ListTaxonAttributesTransformedData>;
}) {
  return usePswQuery({
    ...params.options,
    queryKey: taxonQueriesKeys.taxonAttributes(params.taxonKey, params.suppliers, params.xpath),
    queryFn: () =>
      FetchService.post<ListTaxonAttributesResponse, ListTaxonAttributesBody>('/taxons/attributes', {
        taxonId: params.taxonKey,
        suppliers: params.suppliers,
        xpath: params.xpath,
      }),
    enabled:
      params.options && 'enabled' in params.options
        ? params.options.enabled
        : !!params.taxonKey || (params.suppliers?.length || 0) > 0,
    select: transformRawAttributesData,
  });
}

export function fetchTaxonAttributes(taxonKey: string) {
  return queryClient.fetchQuery<
    ListTaxonAttributesResponse,
    FetchError<ListTaxonAttributesResponse>,
    ListTaxonAttributesTransformedData
  >({
    queryKey: taxonQueriesKeys.taxonAttributes(taxonKey, undefined),
    queryFn: async () => {
      const result = await FetchService.post<ListTaxonAttributesResponse, ListTaxonAttributesBody>(
        '/taxons/attributes',
        {
          taxonId: taxonKey,
        },
      );
      return transformRawAttributesData(result);
    },
  });
}

// =====================================================================================================================
// Get months
// =====================================================================================================================

const transformRawDataToTaxonMonth = (data: Months) => new TaxonMonth(data);

export function useTaxonsMonths(options?: PswQueryOptions<Months, TaxonMonth>) {
  return usePswQuery({
    ...options,
    queryKey: ['taxonsMonths'],
    queryFn: () => FetchService.post<Months>('/taxons/months', {}),
    select: transformRawDataToTaxonMonth,
  });
}

export async function fetchTaxonsMonths() {
  const data = await queryClient.fetchQuery<Months>({
    queryKey: ['taxonsMonths'],
    queryFn: () => FetchService.post('/taxons/months', {}),
  });
  return transformRawDataToTaxonMonth(data);
}

// =====================================================================================================================
// List suppliers
// =====================================================================================================================

export function useSuppliers(taxonomy?: string, options?: PswQueryOptions<ListSuppliersResponse>) {
  return usePswQuery<string[]>({
    ...options,
    queryKey: ['suppliers', taxonomy],
    queryFn: () => FetchService.post<ListSuppliersResponse, ListSuppliersBody>('/taxons/suppliers', { taxonomy }),
  });
}
